import { Formik, FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { EditDischargeDetailsFormData } from '~/lib/types/episode/dischargePlan/EditDischargeDetailsFormData';
import EditDischargeDetailsModal from './EditDischargeDetailsModal';
import { DiscardChangesModalContextProvider } from '../common/DiscardChangesModalContext';
import {
  GetDischargePlanResponse,
  useUpdateDischargePlanDetailsMutation,
} from '~/store/services/clinical-data/dischargePlan';
import {
  coalescePlainTextToEditorV2Html,
  DateFormat,
  FAIconName,
  useToastMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useDischargePlanViewContext } from '../DischargePlanViewContext';

interface EditDischargeDetailsModalFormProps {
  isOpen: boolean;
  onClose: () => void;
  dischargePlanData: GetDischargePlanResponse;
}

const EditDischargeDetailsModalForm = ({
  isOpen,
  onClose,
  dischargePlanData,
}: EditDischargeDetailsModalFormProps) => {
  const formikRef = useRef<FormikProps<EditDischargeDetailsFormData>>(null);
  const { createToast } = useToastMessage();
  const { errors: dischargePlanErrors, setDischargeDetailsErrors } =
    useDischargePlanViewContext();
  const [updateDischargePlanDetails, { isLoading }] =
    useUpdateDischargePlanDetailsMutation();

  const handleSuccess = useCallback(() => {
    createToast({
      icon: FAIconName.checkRegular,
      id: 'update-discharge-plan-details-success_toast',
      text: 'Discharge plan details updated successfully',
      variant: 'success',
    });
    const hasErrors = Object.values(dischargePlanErrors.dischargeDetails).some(
      (error) => error,
    );
    if (hasErrors) {
      setDischargeDetailsErrors({
        isPatientSeekingCare: false,
        levelOfCare: false,
        primaryDischargeReason: false,
        secondaryDischargeReason: false,
        tentativeDischargeDate: false,
        typeOfCare: false,
        whoInitiatedDischarge: false,
      });
    }
  }, [createToast, dischargePlanErrors, setDischargeDetailsErrors]);

  const handleFailure = useCallback(() => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'update-discharge-plan-details-error',
      text: 'Error saving discharge details',
      variant: 'critical',
    });
  }, [createToast]);

  const handleSubmit = useCallback(() => {
    const values = formikRef.current?.values;
    updateDischargePlanDetails({
      dischargePlanDetails: {
        additionalDischargeReasonDetails:
          values?.additionalDischargeReasonDetails,
        isPatientSeekingCareCode: values?.isPatientSeekingCare?.code ?? null,
        levelOfCareCodes: values?.levelOfCare.map((loc) => loc.code) ?? [],
        primaryDischargeReasonExternalId:
          values?.primaryDischargeReason?.statusReasonExternalId ?? null,
        secondaryDischargeReasonCode:
          values?.secondaryDischargeReason?.code ?? null,
        statusAtDischarge: values?.statusAtDischarge,
        tentativeDischargeDate: values?.tentativeDischargeDate
          ? DateTime.fromJSDate(values.tentativeDischargeDate).toFormat(
              DateFormat.shortYearMonthDay,
            )
          : null,
        typeOfCareCodes: values?.typeOfCare.map((toc) => toc.code) ?? [],
        whoInitiatedDischargeCode: values?.whoInitiatedDischarge?.code ?? null,
      },
      dischargePlanId: dischargePlanData.id,
    })
      .unwrap()
      .then(() => {
        handleSuccess();
        onClose();
      })
      .catch(() => handleFailure());
  }, [
    updateDischargePlanDetails,
    dischargePlanData.id,
    onClose,
    handleFailure,
    handleSuccess,
  ]);

  const initialValues = useMemo<EditDischargeDetailsFormData>(() => {
    return {
      additionalDischargeReasonDetails:
        dischargePlanData.additionalDischargeReasonDetails
          ? coalescePlainTextToEditorV2Html(
              dischargePlanData.additionalDischargeReasonDetails,
            )
          : null,
      isPatientSeekingCare: dischargePlanData.isPatientSeekingCare ?? null,
      levelOfCare: dischargePlanData.levelOfCare,
      primaryDischargeReason: dischargePlanData.primaryDischargeReason ?? null,
      secondaryDischargeReason:
        dischargePlanData.secondaryDischargeReason ?? null,
      statusAtDischarge: dischargePlanData.statusAtDischarge
        ? coalescePlainTextToEditorV2Html(dischargePlanData.statusAtDischarge)
        : null,
      tentativeDischargeDate: dischargePlanData.tentativeDischargeDate
        ? DateTime.fromFormat(
            dischargePlanData.tentativeDischargeDate,
            DateFormat.shortYearMonthDay,
          ).toJSDate()
        : null,
      typeOfCare: dischargePlanData.typeOfCare,
      whoInitiatedDischarge: dischargePlanData.whoInitiatedDischarge ?? null,
    };
  }, [dischargePlanData]);

  if (!isOpen) {
    return null;
  }

  return (
    <DiscardChangesModalContextProvider onCloseModal={onClose}>
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        <EditDischargeDetailsModal isLoading={isLoading} isOpen={isOpen} />
      </Formik>
    </DiscardChangesModalContextProvider>
  );
};
export default EditDischargeDetailsModalForm;
